import { useMemo } from "react";
import { Background, BaseEdge, EdgeLabelRenderer, getSmoothStepPath, Handle, MarkerType, Position, ReactFlow, type Edge, type EdgeProps, type Node, type NodeProps } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Bot, Clock, Flag, GitBranch, GitFork, Hourglass, Play, Sparkles, Users, Zap } from "lucide-react";
import { cn } from "../lib/cn";

export interface WorkflowNodeLike {
  key: string;
  label: string;
  labelAr: string;
  kind: "Start" | "Task" | "Decision" | "Wait" | "End" | "Automation" | "Agent" | "SubProcess";
  actors: string[];
  slaDays?: number | null;
  slaWorkingDays?: boolean;
  legalBasis?: string | null;
  agentHooks: string[];
  lane?: number;
  order?: number;
  /** Saved by the Workflow Builder; when every state has one the diagram follows the builder's layout. */
  position?: { x: number; y: number } | null;
  agent?: string | null;
  actions?: number;
}

export interface WorkflowEdgeLike {
  from: string;
  to: string;
  trigger: string;
  label?: string | null;
  labelAr?: string | null;
}

export interface WorkflowDiagramProps {
  nodes: WorkflowNodeLike[];
  edges: WorkflowEdgeLike[];
  currentState?: string;
  visitedStates?: string[];
  locale?: "en" | "ar";
  onNodeClick?: (key: string) => void;
  className?: string;
  roleLabel?: (role: string) => string;
  /** The stage's time limit in the reading language, e.g. "10 أيام عمل". */
  durationLabel?: (days: number, workingDays: boolean) => string;
  height?: number | string;
}

type NcpNodeData = {
  node: WorkflowNodeLike;
  state: "current" | "visited" | "future";
  locale: "en" | "ar";
  roleLabel: (r: string) => string;
  durationLabel: (days: number, workingDays: boolean) => string;
};

const kindIcon = { Start: Play, Task: Users, Decision: GitBranch, Wait: Hourglass, End: Flag, Automation: Zap, Agent: Sparkles, SubProcess: GitFork };

function NcpNode({ data }: NodeProps<Node<NcpNodeData>>) {
  const { node, state, locale, roleLabel, durationLabel } = data;
  const Icon = kindIcon[node.kind] ?? Users;
  const label = locale === "ar" && node.labelAr ? node.labelAr : node.label;
  const isTerminal = node.kind === "Start" || node.kind === "End";
  return (
    <div
      dir={locale === "ar" ? "rtl" : "ltr"}
      className={cn(
        "relative w-[220px] rounded-xl border bg-white px-3 py-2.5 text-start shadow-sand transition-all",
        node.kind === "Decision" && "border-dashed",
        isTerminal && "w-[160px] rounded-full text-center",
        state === "current" && "border-aegold-500 ring-4 ring-aegold-200/70 animate-pulse-gold",
        state === "visited" && "border-success-300 bg-success-50/40",
        state === "future" && "border-sand-300 opacity-90",
      )}
    >
      <Handle type="target" position={Position.Top} className="!size-2 !bg-sand-400 !border-0" />
      <div className={cn("flex items-start gap-2", isTerminal && "justify-center")}>
        <span className={cn("mt-0.5 grid size-6 shrink-0 place-items-center rounded-md", state === "current" ? "bg-aegold-600 text-white" : state === "visited" ? "bg-success-600 text-white" : "bg-sand-200 text-sand-700")}>
          <Icon className="size-3.5" />
        </span>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold leading-snug text-aeblack-950">{label}</p>
          {!isTerminal && node.actors.length > 0 ? <p className="mt-0.5 truncate text-[10px] text-sand-700">{node.actors.map(roleLabel).join(" · ")}</p> : null}
        </div>
      </div>
      {!isTerminal && (node.slaDays || node.agentHooks.length > 0 || node.agent || node.actions) ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {node.slaDays ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-sand-100 px-1.5 py-0.5 text-[10px] text-sand-800">
              <Clock className="size-3" /> {durationLabel(node.slaDays, !!node.slaWorkingDays)}
            </span>
          ) : null}
          {node.agentHooks.length > 0 || node.agent ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-aegold-100 to-seablue-100 px-1.5 py-0.5 text-[10px] text-aeblack-900" title={[...node.agentHooks, ...(node.agent ? [node.agent] : [])].join(", ")}>
              <Bot className="size-3" /> {node.agentHooks.length + (node.agent ? 1 : 0)}
            </span>
          ) : null}
          {node.actions ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-seablue-50 px-1.5 py-0.5 text-[10px] text-seablue-800"><Zap className="size-3" /> {node.actions}</span>
          ) : null}
        </div>
      ) : null}
      <Handle type="source" position={Position.Bottom} className="!size-2 !bg-sand-400 !border-0" />
    </div>
  );
}

function NcpEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, data, markerEnd }: EdgeProps) {
  const [path, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, borderRadius: 12 });
  const active = (data as { active?: boolean } | undefined)?.active;
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={{ stroke: active ? "var(--color-aegold-500)" : "var(--color-sand-400)", strokeWidth: active ? 2 : 1.5 }} />
      {label ? (
        <EdgeLabelRenderer>
          <div style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }} className="nodrag nopan pointer-events-none absolute rounded-md border border-sand-200 bg-white/95 px-1.5 py-0.5 text-[10px] text-sand-800 shadow-sm">
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

const nodeTypes = { ncp: NcpNode };
const edgeTypes = { ncp: NcpEdge };

/**
 * Layered layout: the column (x) is the longest forward path from the start node (back edges ignored),
 * the row (y) is the lane hint (0 = applicant/exception lane, 1 = main lane, 2 = optional branch).
 * Reads left-to-right, which matches the RFP swim-lane diagrams once rotated.
 */
function layout(nodes: WorkflowNodeLike[], edges: WorkflowEdgeLike[]): Map<string, { x: number; y: number }> {
  const pos = new Map<string, { x: number; y: number }>();
  if (nodes.length > 0 && nodes.every((n) => n.position)) {
    for (const n of nodes) pos.set(n.key, { x: n.position!.x, y: n.position!.y });
    return pos;
  }
  const colWidth = 270;
  const rowHeight = 150;
  const keys = nodes.map((n) => n.key);
  const start = nodes.find((n) => n.kind === "Start")?.key ?? keys[0];
  const level = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  for (const e of edges) outgoing.set(e.from, [...(outgoing.get(e.from) ?? []), e.to]);

  // Longest path via DFS with an on-stack guard to break cycles (e.g. RequestCompletion loops).
  const onStack = new Set<string>();
  const visit = (key: string, depth: number) => {
    if (onStack.has(key)) return;
    if ((level.get(key) ?? -1) >= depth) return;
    level.set(key, depth);
    onStack.add(key);
    for (const next of outgoing.get(key) ?? []) visit(next, depth + 1);
    onStack.delete(key);
  };
  if (start) visit(start, 0);
  for (const k of keys) if (!level.has(k)) level.set(k, 0);

  // Wrap long chains into row blocks of `perRow` levels so the graph stays readable; lanes offset within a block.
  const perRow = 5;
  const laneCount = Math.max(1, ...nodes.map((n) => (n.lane ?? 1) + 1));
  const blockHeight = laneCount * rowHeight;
  const used = new Map<string, number>();
  for (const n of nodes) {
    const lvl = level.get(n.key) ?? 0;
    const block = Math.floor(lvl / perRow);
    const colInBlock = lvl % perRow;
    const x = (block % 2 === 0 ? colInBlock : perRow - 1 - colInBlock) * colWidth; // serpentine for shorter edges
    const lane = n.lane ?? 1;
    const slot = `${lvl}:${lane}`;
    const bump = used.get(slot) ?? 0;
    used.set(slot, bump + 1);
    pos.set(n.key, { x, y: block * blockHeight + lane * rowHeight + bump * 70 });
  }
  return pos;
}

export function WorkflowDiagram({ nodes, edges, currentState, visitedStates = [], locale = "en", onNodeClick, className, roleLabel = (r) => r, durationLabel = (d, w) => `${d}${w ? "wd" : "d"}`, height = 560 }: WorkflowDiagramProps) {
  const rfNodes = useMemo<Node<NcpNodeData>[]>(() => {
    const positions = layout(nodes, edges);
    return nodes.map((n) => ({
      id: n.key,
      type: "ncp",
      position: positions.get(n.key) ?? { x: 0, y: 0 },
      data: { node: n, state: n.key === currentState ? "current" : visitedStates.includes(n.key) ? "visited" : "future", locale, roleLabel, durationLabel },
      draggable: false,
    }));
  }, [nodes, edges, currentState, visitedStates, locale, roleLabel, durationLabel]);

  const rfEdges = useMemo<Edge[]>(
    () =>
      edges.map((e, i) => {
        const active = visitedStates.includes(e.from) && (visitedStates.includes(e.to) || e.to === currentState);
        return {
          id: `${e.from}-${e.to}-${e.trigger}-${i}`,
          source: e.from,
          target: e.to,
          type: "ncp",
          label: locale === "ar" && e.labelAr ? e.labelAr : e.label ?? undefined,
          data: { active },
          markerEnd: { type: MarkerType.ArrowClosed, color: active ? "#b68a35" : "#cfc5b1", width: 16, height: 16 },
        };
      }),
    [edges, visitedStates, currentState, locale],
  );

  return (
    <div className={cn("surface overflow-hidden", className)} style={{ height }} dir="ltr">
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.3}
        maxZoom={1.4}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false}
        elementsSelectable
        onNodeClick={(_, n) => onNodeClick?.(n.id)}
      >
        <Background color="#e3dccd" gap={20} size={1} />
      </ReactFlow>
    </div>
  );
}
